import { useTradingDocument } from "api/trading-documents/hooks";
import { CommonError, DisabledOpacity, MockupLoader } from "components/utils";
import { DrawerRightPanel, RightPanelBody } from "components/utils/drawer";
import { useQuery } from "hooks";
import { CorrectionSection } from "pages/tradingDocuments/shared/components/rightPanel/correctionSection/CorrectionSection";
import { CorrespondingDocumentSection } from "pages/tradingDocuments/shared/components/rightPanel/correspondingDocumentsSection/CorrespondingDocumentSection";
import { GeneralInfoSection } from "pages/tradingDocuments/shared/components/rightPanel/generalInfoSection/GeneralInfoSection";
import { NotificationSection } from "pages/tradingDocuments/shared/components/rightPanel/notificationSection/NotificationSection";
import { PaymentSection } from "pages/tradingDocuments/shared/components/rightPanel/paymentSection/PaymentSection";
import { RecipientInfoSection } from "pages/tradingDocuments/shared/components/rightPanel/recipientInfoSection/RecipientInfoSection";
import { TitleSection } from "pages/tradingDocuments/shared/components/rightPanel/titleSection/TitleSection";
import { AmountSection } from "./components/AmountSection";
import { ItemsSummarySection } from "./components/ItemsSummarySection";
import { MasterDocumentSection } from "./components/MasterDocumentSection";
import { PanelHeader } from "./components/PanelHeader";
import { useEffect, useRef, useState } from "react";
import { RemindersSection } from "../../shared/components/rightPanel/remindersSection/RemindersSection";
import { CommentsGenericSection } from "components/common/commentsGeneric/CommentsGenericSection";

interface Props {
  close: () => void;
}

export const RightPanel = ({ close }: Props) => {
  const { query } = useQuery();
  const { panelId } = query;
  const [isScrolledOver, setIsScrolledOver] = useState(false);
  const clearCommentsRef = useRef<HTMLDivElement>(null);
  const { data: correctionInvoice, error, isLoading } = useTradingDocument(
    { id: panelId },
    { enabled: Boolean(panelId) },
  );

  useEffect(() => {
    setIsScrolledOver(false);
  }, [panelId]);

  if (error) {
    return (
      <DrawerRightPanel>
        <CommonError status={error._httpStatus_} />
      </DrawerRightPanel>
    );
  }

  if (isLoading) {
    return (
      <DrawerRightPanel>
        <MockupLoader type="drawer" />
      </DrawerRightPanel>
    );
  }

  if (!correctionInvoice) return null;

  return (
    <DrawerRightPanel key={correctionInvoice.id}>
      <PanelHeader close={close} isScrolledOver={isScrolledOver} />
      <DisabledOpacity disabled={isLoading}>
        <RightPanelBody
          myRef={clearCommentsRef}
          setIsScrolledOver={setIsScrolledOver}
          hasCommentsModule
        >
          <TitleSection tradingDocument={correctionInvoice} />
          <GeneralInfoSection tradingDocument={correctionInvoice} />
          <AmountSection correctionInvoice={correctionInvoice} />
          <ItemsSummarySection tradingDocument={correctionInvoice} />
          <MasterDocumentSection correctionInvoice={correctionInvoice} />
          <PaymentSection tradingDocument={correctionInvoice} />
          <NotificationSection tradingDocument={correctionInvoice} />
          {correctionInvoice.corrections.length > 0 && (
            <CorrectionSection tradingDocument={correctionInvoice} />
          )}
          {correctionInvoice.correspondingDocuments.length > 0 && (
            <CorrespondingDocumentSection tradingDocument={correctionInvoice} />
          )}
          <RemindersSection tradingDocument={correctionInvoice} />
          <RecipientInfoSection tradingDocument={correctionInvoice} />
        </RightPanelBody>
        <CommentsGenericSection
          outsideRef={clearCommentsRef}
          paramName="tradingDocument"
          endpointUrl="/finances/comments/items"
          commentedObject={correctionInvoice}
        />
      </DisabledOpacity>
    </DrawerRightPanel>
  );
};
